import { Injectable, NgZone } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { fromEvent, merge, Subject, Subscription } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class IdleActivityService {
  /** Seconds of inactivity before pausing silent refresh */
  private readonly IDLE_THRESHOLD_SECONDS = 60;

  private destroy$ = new Subject<void>();
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private isIdle = false;
  private activitySubscription: Subscription | null = null;

  constructor(
    private ngZone: NgZone,
    private oauthService: OAuthService
  ) {}

  /**
   * Begin tracking user activity.
   * Call after setupAutomaticSilentRefresh() in AuthService.configure().
   */
  start(): void {
    // Prevent duplicate listeners
    this.stop();

    this.ngZone.runOutsideAngular(() => {
      const activity$ = merge(
        fromEvent(document, 'mousemove'),
        fromEvent(document, 'keydown'),
        fromEvent(document, 'click'),
        fromEvent(document, 'scroll'),
        fromEvent(document, 'touchstart')
      ).pipe(
        debounceTime(1000),
        takeUntil(this.destroy$)
      );

      this.activitySubscription = activity$.subscribe(() => {
        this.onActivity();
      });

      // Start the initial idle countdown
      this.resetIdleTimer();
    });
  }

  /** Stop tracking and clean up all listeners / timers. */
  stop(): void {
    this.destroy$.next();
    this.destroy$.complete();
    // Re-create so start() can be called again later
    this.destroy$ = new Subject<void>();

    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }

    this.activitySubscription?.unsubscribe();
    this.activitySubscription = null;
    this.isIdle = false;
  }

  private resetIdleTimer(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
    }

    this.idleTimer = setTimeout(() => {
      this.onIdle();
    }, this.IDLE_THRESHOLD_SECONDS * 1000);
  }

  private onActivity(): void {
    if (this.isIdle) {
      this.isIdle = false;
      console.log('[IdleActivityService] User active — resumed silent refresh');

      this.ngZone.run(() => {
        this.oauthService.setupAutomaticSilentRefresh();
        this.oauthService.silentRefresh().catch((err) => {
          console.warn('[IdleActivityService] Immediate silent refresh failed:', err);
        });
      });
    }

    this.resetIdleTimer();
  }

  private onIdle(): void {
    this.isIdle = true;
    console.log('[IdleActivityService] User idle — paused silent refresh');
    this.oauthService.stopAutomaticRefresh();
  }
}
